import Layout from '@/components/feature/Layout';
import { companyInfo } from '@/mocks/company';
import { Link } from 'react-router-dom';

const whatsappMessage = encodeURIComponent('Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre máquinas de solda de geomembrana.');
const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Máquina de Solda de Geomembrana Preço',
  description: 'Conheça os fatores que definem o preço de uma máquina de solda geomembrana no Brasil.',
  url: 'https://maquinadesoldageomembrana.com.br/maquina-de-solda-de-geomembrana-preco',
  inLanguage: 'pt-BR',
};

export default function PrecoPage() {
  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <nav className="text-xs text-foreground-400 mb-6">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <Link to="/maquina-de-solda-de-geomembrana" className="hover:text-primary-600 cursor-pointer">Máquinas</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <span className="text-foreground-600">Preço</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Máquina de Solda de Geomembrana Preço
            </h1>

            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                O <strong className="text-foreground-950">preço de uma máquina de solda de geomembrana</strong> no Brasil varia conforme o tipo de equipamento, potência, marca e acessórios incluídos. Abaixo explicamos os fatores que determinam o valor e como solicitar um orçamento personalizado para seu projeto.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Fatores que Definem o Preço</h2>

              <div className="space-y-4">
                {[
                  { num: '01', title: 'Tipo de Tecnologia', desc: 'Máquinas automáticas com tração motorizada custam mais que modelos manuais. Extrusoras também têm faixa própria.' },
                  { num: '02', title: 'Potência e Espessura', desc: 'Equipamentos para geomembrana de 3,0 mm exigem mais potência e custam mais que modelos para 0,5 mm.' },
                  { num: '03', title: 'Marca e Qualidade', desc: 'Marcas com certificação internacional e peças originais de fábrica têm preço justificado pela durabilidade.' },
                  { num: '04', title: 'Acessórios Inclusos', desc: 'Maleta de transporte, peças de reposição, consumíveis e treinamento podem alterar o preço final.' },
                  { num: '05', title: 'Volume e Frete', desc: 'Projetos com múltiplas máquinas podem ter condições especiais. Frete varia conforme a localidade.' },
                  { num: '06', title: 'Garantia e Suporte', desc: 'Equipamentos com garantia estendida e suporte técnico pós-venda podem ter valor diferenciado.' },
                ].map((f) => (
                  <div key={f.num} className="flex items-start gap-4 bg-background-100 rounded-xl border border-background-200/70 p-5">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-white text-sm font-bold flex-shrink-0">{f.num}</span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900 uppercase mb-1">{f.title}</h3>
                      <p className="text-foreground-500 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Como Solicitar Orçamento</h2>
              <p>
                Para receber um orçamento preciso, informe:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Tipo de material (PEAD, PVC, LLDPE) e espessura</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Área total do projeto (m²)</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Tipo de terreno (plano, inclinado, acesso restrito)</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span>Prazo de entrega e estado de destino</span></li>
              </ul>
              <p>
                Nossa equipe técnica analisa esses dados e indica o equipamento ideal, sem compromisso.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              <Link to="/comprar-maquina-de-solda-de-geomembrana" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Ver Página de Compra</Link>
              <Link to="/modelos-de-maquinas" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Ver Todos os Modelos</Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-primary-50 rounded-2xl border border-primary-200/70 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950 uppercase mb-3">Solicitar Orçamento Gratuito</h3>
              <p className="text-foreground-500 text-sm leading-relaxed mb-5">Resposta em minutos via WhatsApp com equipamento ideal para seu projeto.</p>
              <a href={whatsappUrl} target="_blank" rel="nofollow noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide">
                <i className="ri-whatsapp-line" /> Pedir Orçamento
              </a>
            </div>

            <div className="bg-white rounded-2xl border border-background-200/70 p-5">
              <h4 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-3">Formas de Pagamento</h4>
              <ul className="space-y-2 text-sm text-foreground-600">
                <li className="flex items-center gap-2"><i className="ri-bank-card-line text-primary-500" /> Cartão de crédito (até 12x)</li>
                <li className="flex items-center gap-2"><i className="ri-barcode-line text-primary-500" /> Boleto bancário</li>
                <li className="flex items-center gap-2"><i className="ri-building-line text-primary-500" /> Transferência / PIX</li>
                <li className="flex items-center gap-2"><i className="ri-file-list-line text-primary-500" /> Orçamento para empresa</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-background-200/70 p-5">
              <h4 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-3">Perguntas Frequentes</h4>
              <div className="space-y-3">
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                    Entrega para todo o Brasil?
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="text-foreground-500 text-xs mt-2">Sim. Enviamos para todos os estados via transportadora. Frete calculado no orçamento.</p>
                </details>
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                    Tem garantia?
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="text-foreground-500 text-xs mt-2">Garantia de 6 a 12 meses contra defeitos de fabricação, com peças originais de fábrica.</p>
                </details>
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                    Parcelamento sem juros?
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="text-foreground-500 text-xs mt-2">Consulte condições especiais para empresas e projetos com múltiplas unidades.</p>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}